// Dati di Worklogs V2: worklog del periodo per ambito (mie, progetti, persone) e progetti della lista.
import { API_ENDPOINTS } from '../config/api.config';

// Il periodo si carica tutto (filtri, totali e raggruppamenti sono lato client), a blocchi:
// una risposta Lambda non può superare 6 MB (~700 byte per worklog)
export const ROW_LIMIT = 20000;
const CHUNK_SIZE = 2000;

async function request(url) {
  const res = await fetch(url);
  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    body = null;
  }
  if (!res.ok) throw new Error(body?.error || body?.message || `Request failed (${res.status})`);
  return body || {};
}

/**
 * Worklog del periodo (date incluse, YYYY-MM-DD), filtrati per utente, utenti o progetti.
 * Il primo blocco dà il totale, gli altri si caricano in parallelo (ordine stabile: data e id).
 * @returns {Promise<{ worklogs: Array, total: number }>} total può superare ROW_LIMIT
 */
export async function fetchWorklogs({ dateFrom, dateTo, userId, userIds, projectIds }) {
  const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, sort_field: 'date', sort_dir: 'desc' });
  if (userId) params.set('user_id', String(userId));
  if (userIds?.length) params.set('user_ids', userIds.join(','));
  if (projectIds?.length) params.set('project_ids', projectIds.join(','));
  const page = (skip) => request(`${API_ENDPOINTS.WORKLOGS}?${params}&limit=${CHUNK_SIZE}&skip=${skip}`);

  const first = await page(0);
  const total = first.total ?? (first.worklogs || []).length;
  const skips = [];
  for (let skip = CHUNK_SIZE; skip < Math.min(total, ROW_LIMIT); skip += CHUNK_SIZE) skips.push(skip);
  const rest = await Promise.all(skips.map(page));
  const worklogs = [first, ...rest].flatMap((d) => d.worklogs || []).map((w) => ({ ...w, dateKey: String(w.date).slice(0, 10) }));
  return { worklogs, total };
}

const isTeamLeaderFor = (project) => String(project?.project_role?.code || '').toUpperCase() === 'T';

/** Ruolo di gestione sul progetto (Service Manager / JR SM); senza ruolo sul progetto vale solo per l'Admin. */
export function isManagerFor(project, isAdmin) {
  if (!project?.project_role) return isAdmin;
  if (isTeamLeaderFor(project)) return false;
  return [1, 2].includes(project.project_role.id);
}

/**
 * Progetti della lista, stessa regola della pagina classica:
 * - Delivery Manager: tutti i progetti
 * - Admin / Service Manager / JR SM / Team Leader: progetti con ruolo 1,2,3 ancora validi
 * - altri ruoli: progetti dell'utente
 */
export async function fetchListProjects({ seesAllProjects, managesProjects, userId }) {
  if (seesAllProjects) {
    const data = await request(API_ENDPOINTS.PROJECTS);
    return data.projects || [];
  }
  if (managesProjects) {
    const data = await request(`${API_ENDPOINTS.PROJECT_USERS}?user_id=${userId}&role_ids=1,2,3`);
    const now = new Date();
    return (data.user_projects_with_references || [])
      .filter((item) => !item.validity_end_date || new Date(item.validity_end_date) > now)
      .map((item) => ({ ...item.project, customer: item.customer, project_role: item.project_role }));
  }
  const data = await request(`${API_ENDPOINTS.PROJECTS}?user_id=${userId}`);
  return data.projects || [];
}

/**
 * Progetti da interrogare, come nella pagina classica:
 * - Delivery Manager: solo quelli scelti (nessuna scelta = nessun caricamento)
 * - Admin / SM / JR SM / TL: quelli scelti (tutti se nessuna scelta) dove gestisce o è Team Leader
 * - altri ruoli: nessuno
 */
export function queryProjectIds(projects, selectedIds, { seesAllProjects, managesProjects, isAdmin }) {
  if (seesAllProjects) return selectedIds;
  if (!managesProjects) return [];
  const chosen = selectedIds.length ? projects.filter((p) => selectedIds.includes(p.id)) : projects;
  return chosen.filter((p) => isAdmin || isTeamLeaderFor(p) || isManagerFor(p, isAdmin)).map((p) => p.id);
}
